/**
 * Kunden-Export-Formatierung für PDF/UI.
 *
 * WICHTIG: CSVs dürfen diese Dust-Texte (<0,000001) NIEMALS verwenden.
 * Maschinenlesbare CSV-Spalten immer als volle vorzeichenbehaftete Rohzahlen
 * serialisieren (siehe ExportButton.formatPlainNumber).
 */

const QTY_DUST = 1e-6;
const FIAT_DUST = 1e-5;

/**
 * Einheitliche UTC-Zeit für Steuer- und Ledger-CSVs.
 * Beispiel: "24.07.2026, 20:10:20 UTC"
 *
 * Kein toLocaleString — feste UTC-Methoden, damit Disposal und Ledger
 * für denselben Timestamp exakt übereinstimmen.
 */
export function formatCsvDateTimeUtc(timestamp) {
  const raw = Number(timestamp || 0);

  if (!Number.isFinite(raw) || raw <= 0) {
    return "";
  }

  const milliseconds = raw > 1e12 ? raw : raw * 1000;
  const date = new Date(milliseconds);

  if (!Number.isFinite(date.getTime())) {
    return "";
  }

  const pad = (value) => String(value).padStart(2, "0");

  const datePart = `${pad(date.getUTCDate())}.${pad(date.getUTCMonth() + 1)}.${date.getUTCFullYear()}`;
  const timePart = `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;

  return `${datePart}, ${timePart} UTC`;
}

/**
 * Finale Steuerklassifikation für Exporte (CSV/PDF/Ledger).
 * B3TR/VEB3TR-Protokoll-"airdrop" wird immer als x2earn_reward_claim ausgewiesen.
 */
export function normalizeTaxClassificationForExport(classification, token = "") {
  const cls = String(classification || "").trim().toLowerCase();
  const tok = String(token || "").trim().toUpperCase();

  if (!cls) return "";

  if (cls.includes("x2earn")) {
    return "x2earn_reward_claim";
  }

  const isB3trLike = tok === "B3TR" || tok === "VEB3TR";
  if (isB3trLike && (cls === "airdrop" || cls === "x2earn_reward")) {
    return "x2earn_reward_claim";
  }

  return cls;
}

export function isExactZeroAmount(v) {
  const n = Number(v);
  return Number.isFinite(n) && n === 0;
}

/**
 * PDF/UI-only: Kleinstmengen mit Vorzeichen.
 * Positiv: "<0,000001" / negativ: ">-0,000001" (Vorzeichen bleibt erkennbar).
 * Exakt 0 → "0". CSVs dürfen diese Texte NIEMALS verwenden.
 */
export function formatExportQuantity(value, { language = 'de', maxDecimals = 8 } = {}) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  if (n === 0) return '0';
  const abs = Math.abs(n);
  if (abs > 0 && abs < QTY_DUST) {
    if (n < 0) return language === 'en' ? '>-0.000001' : '>-0,000001';
    return language === 'en' ? '<0.000001' : '<0,000001';
  }
  // Keine trailing zeros erzwingen; begrenzte Präzision ohne Down-Round auf 0
  const s = trimTrailingZeros(n.toFixed(maxDecimals));
  if (language === 'de') return s.replace('.', ',');
  return s;
}

export function formatExportFiat(value, { language = 'de', maxDecimals = 8 } = {}) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  if (n === 0) return '0';
  const abs = Math.abs(n);
  if (abs > 0 && abs < FIAT_DUST) {
    if (n < 0) return language === 'en' ? '>-0.00001' : '>-0,00001';
    return language === 'en' ? '<0.00001' : '<0,00001';
  }
  const s = trimTrailingZeros(n.toFixed(maxDecimals));
  if (language === 'de') return s.replace('.', ',');
  return s;
}

function trimTrailingZeros(s) {
  if (!s.includes('.')) return s;
  return s.replace(/\.?0+$/, '');
}

/**
 * Kompakte Event-Anzeige für PDF: "C3 / E2" oder "C3 / E2 / Leg 4"
 */
export function formatCompactEventLabel(row = {}) {
  const clause = row.clause_index ?? row.clauseIndex;
  const event = row.event_index ?? row.eventIndex ?? row.log_index ?? row.logIndex;
  const leg = row.fifo_leg_index ?? row.fifoLegIndex ?? row.leg_index ?? row.legIndex;

  const hasClause = clause != null && clause !== '' && Number.isFinite(Number(clause));
  const hasEvent = event != null && event !== '' && Number.isFinite(Number(event));
  if (!hasClause && !hasEvent) return null;

  const parts = [];
  if (hasClause) parts.push(`C${Number(clause)}`);
  if (hasEvent) parts.push(`E${Number(event)}`);
  if (leg != null && leg !== '' && Number.isFinite(Number(leg))) {
    parts.push(`Leg ${Number(leg)}`);
  }
  return parts.length ? `Event: ${parts.join(' / ')}` : null;
}

export function extractEventIdentityFields(row = {}) {
  const raw = row.raw_json || row.rawJson || {};
  const clause =
    row.clause_index ??
    row.clauseIndex ??
    raw.clause_index ??
    raw.clauseIndex ??
    null;
  const event =
    row.event_index ??
    row.eventIndex ??
    row.log_index ??
    row.logIndex ??
    raw.event_index ??
    raw.eventIndex ??
    raw.log_index ??
    raw.logIndex ??
    null;
  const log =
    row.log_index ?? row.logIndex ?? raw.log_index ?? raw.logIndex ?? event;
  const contract = String(
    row.token_contract ||
      row.tokenContract ||
      row.nftContract ||
      row.nft_contract ||
      row.contract ||
      row.emitter ||
      raw.contract ||
      raw.token_contract ||
      raw.nftContract ||
      ''
  ).toLowerCase() || null;

  const canonical =
    row.canonical_event_key ||
    row.canonicalEventKey ||
    row.Event_Key ||
    row.event_key ||
    null;

  const disposalKey =
    row.disposal_event_key ||
    row.disposalEventKey ||
    null;

  const fifoLeg =
    row.fifo_leg_index ?? row.fifoLegIndex ?? row.leg_index ?? row.legIndex ?? null;

  const toNumOrNull = (v) => {
    if (v == null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  return {
    clause_index: toNumOrNull(clause),
    event_index: toNumOrNull(event),
    log_index: toNumOrNull(log),
    token_contract: contract,
    canonical_event_key: canonical != null ? String(canonical) : null,
    disposal_event_key: disposalKey != null ? String(disposalKey) : null,
    fifo_leg_index: toNumOrNull(fifoLeg),
  };
}

/** Normalisiert snake_case Event-Identität auf Tax-/Export-Rows (ohne Berechnung zu ändern). */
export function attachEventIdentityFields(row = {}) {
  const id = extractEventIdentityFields(row);
  const out = { ...row };
  if (id.clause_index != null) {
    out.clause_index = id.clause_index;
    if (out.clauseIndex == null) out.clauseIndex = id.clause_index;
  }
  if (id.event_index != null) {
    out.event_index = id.event_index;
    if (out.eventIndex == null) out.eventIndex = id.event_index;
  }
  if (id.log_index != null) {
    out.log_index = id.log_index;
    if (out.logIndex == null) out.logIndex = id.log_index;
  }
  if (id.token_contract) {
    out.token_contract = id.token_contract;
  }
  if (id.canonical_event_key) {
    out.canonical_event_key = id.canonical_event_key;
    if (out.canonicalEventKey == null) {
      out.canonicalEventKey = id.canonical_event_key;
    }
  }
  if (id.disposal_event_key) {
    out.disposal_event_key = id.disposal_event_key;
    if (out.disposalEventKey == null) {
      out.disposalEventKey = id.disposal_event_key;
    }
  }
  if (id.fifo_leg_index != null) {
    out.fifo_leg_index = id.fifo_leg_index;
    if (out.fifoLegIndex == null) {
      out.fifoLegIndex = id.fifo_leg_index;
    }
  }
  return out;
}

/**
 * Kundenzeilen: exakt amount===0 aussortieren (außer forceKeepZeroRow).
 */
export function shouldIncludeCustomerExportRow(row = {}, opts = {}) {
  if (row.forceKeepZeroRow === true || opts.allowZero === true) return true;
  const amount = Number(
    row.Menge ?? row.amount ?? row.qty ?? row.quantity ?? row.value ?? NaN
  );
  if (Number.isFinite(amount) && amount === 0) return false;
  return true;
}

export { QTY_DUST, FIAT_DUST };
